import JSZip from 'jszip';

import type { PhotoItemIRepository } from './entity/photoItemIRepository';
import type { PhotoOrderEntity } from './entity/photoOrderEntity';
import type { PhotoOrderIRepository } from './entity/photoOrderIRepository';
import type { PhotoSalesIRepository } from './entity/photoSalesIRepository';
import type { ChildId } from './entity/valueObjects/childId';
import type { NumberOfItems } from './entity/valueObjects/numberOfItems';
import type { PhotoId } from './entity/valueObjects/photoId';
import type { PhotoItemId } from './entity/valueObjects/photoItemId';
import type { SalesGroupId } from './entity/valueObjects/salesGroupId';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';
import type { PhotoAppJson } from '@/types/domain/photo/entities/photoAppEntity';
import type { SerializedError } from '@reduxjs/toolkit';

import { DomainServiceBase } from '@/domain/domainServiceBase';
import { eHandle } from '@/errors';
import { EntityName } from '@/infra/appsync';

/**
 * 写真販売ドメインサービス
 */
export class PhotoDomainService extends DomainServiceBase {
  private wagakoNurseryApi!: WagakoNurseryIApi;

  private photoItemRepository!: PhotoItemIRepository;

  private photoOrderRepository!: PhotoOrderIRepository;

  private photoSalesRepository!: PhotoSalesIRepository;

  /**
   * Injection
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param photoItemRepository photoItemRepository
   * @param photoOrderRepository photoOrderRepository
   * @param photoSalesRepository photoSalesRepository
   */
  inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    photoItemRepository: PhotoItemIRepository,
    photoOrderRepository: PhotoOrderIRepository,
    photoSalesRepository: PhotoSalesIRepository,
  ) {
    this.wagakoNurseryApi = wagakoNurseryApi;
    this.photoItemRepository = photoItemRepository;
    this.photoOrderRepository = photoOrderRepository;
    this.photoSalesRepository = photoSalesRepository;
  }

  /**
   * 販売リスト取得
   * @param token トークン
   * @returns リスト
   */
  public async getPhotoSalesList(token: string | null) {
    const result = await this.photoSalesRepository.getList(token);
    return result;
  }

  /**
   * 写真のurlを取得する 購入確定写真
   * @param nurseryId 施設ID
   * @param photoOrder PhotoOrderEntity
   * @returns NurseryApiSpecialGetPhotoOrdered.PhotoUrlType[] | undefined
   */
  public async getOrderedPhotoUrls(
    nurseryId: string,
    photoOrder: PhotoOrderEntity,
  ) {
    const { items } = await this.wagakoNurseryApi.getOrderedPhotoUrlFromS3(
      nurseryId,
      photoOrder.createDatetime.value.toJSON(),
      photoOrder.salesGroupId.value,
      photoOrder.lastModifiedDatetime.value.toJSON(),
    );
    return items;
  }

  /**
   * 写真のurlリストから画像をダウンロードしてZIPとしてダウンロード
   * @param photoList { url: string }[]
   */
  public async downloadPhotoFiles(
    filename: string,
    photoList: { url: string }[],
    finishedMessage: (message: string, type: 'info' | 'error') => void,
  ) {
    // 写真ダウンロード
    const promises = photoList.map(async (v, index) => {
      const splitUrl = v.url.split('?')[0].split('/');
      const splitFilename = splitUrl[splitUrl.length - 1].split('.');
      const ext = splitFilename[splitFilename.length - 1];
      const name = `${index}.${ext}`;
      finishedMessage(`${name}のダウンロード開始`, 'info');
      try {
        const response = await this.wagakoNurseryApi.fetchS3(v.url);
        if (response.status === 200) {
          const content = response.data;
          if (content) {
            finishedMessage(`${name}のダウンロード完了`, 'info');
            return { name, content };
          }
        }
      } catch (e) {
        eHandle('', 'S3 Download', e as SerializedError);
      }
      finishedMessage(`${name}のダウンロード失敗`, 'error');
      return undefined;
    });
    const nameContentPairs = await Promise.all(promises);
    // zipへの格納
    finishedMessage('ZIPファイル生成開始', 'info');
    const zip = new JSZip();
    const folder = zip.folder(filename);
    const errorList: string[] = [];
    if (!folder) {
      return false;
    }
    nameContentPairs.forEach((nameContentPair, index) => {
      if (nameContentPair) {
        folder.file(nameContentPair.name, nameContentPair.content);
      } else {
        errorList.push(`${index + 1}`);
      }
    });
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    finishedMessage('ZIPファイル生成完了', 'info');
    // ダウンロードリンク生成
    {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `${filename}.zip`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    if (errorList.length === 0) {
      finishedMessage('ダウンロード完了', 'info');
    } else {
      finishedMessage(`${errorList.join(',')}番目の画像のダウンロードに失敗`, 'error');
    }
    return true;
  }

  /**
   * 写真のurlリストから１枚選択してダウンロード
   * @param photo { url: string }
   */
  public async downloadPhotoFile(
    filename: string,
    photo: { url: string },
    finishedMessage: (message: string, type: 'info' | 'error') => void,
  ) {
    // 写真ダウンロード
    const splitUrl = photo.url.split('?')[0].split('/');
    const splitFilename = splitUrl[splitUrl.length - 1].split('.');
    const ext = splitFilename[splitFilename.length - 1];
    const name = `${filename}.${ext}`;
    finishedMessage(`${name}のダウンロード開始`, 'info');
    try {
      const response = await this.wagakoNurseryApi.fetchS3(photo.url);
      if (response.status === 200) {
        const content = response.data;
        if (content) {
          const newBlob = new Blob([content]);
          // ダウンロードリンク生成
          {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(newBlob);
            a.download = name;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
          finishedMessage(`${name}のダウンロード完了`, 'info');
        }
      }
    } catch (e) {
      eHandle('', 'S3 Download', e as SerializedError);
      finishedMessage(`${name}のダウンロード失敗`, 'error');
    }
  }

  /**
   * loadPhotoDataSample
   * @param json json
   * @param nurseryId 施設ID
   * @returns BASE64
   */
  public async loadPhotoDataSample(
    json: PhotoAppJson,
    nurseryId: string,
  ) {
    const entityName = EntityName.WagakoNurseryPhotoPhotoEntity;
    const path = `${entityName}/${json.photoId}/sample.webp`;
    const { data } = await this.wagakoNurseryApi.loadFileFromS3(path, nurseryId);
    const base64 = this.convertArrayBufferToBase64(data);
    return `data:image/webp;base64,${base64}`;
  }

  /**
   * 購入写真のサムネイルをBase64で読み込む
   * @param json json
   * @param nurseryId nurseryId
   * @returns base64url
   */
  public async loadPurchasePhotoDataThumbnail(
    json: PhotoAppJson,
    nurseryId: string,
  ) {
    const entityName = EntityName.WagakoNurseryPhotoPhotoEntity;
    const path = `${entityName}/${json.photoId}/thumbnail.webp`;
    const { data } = await this.wagakoNurseryApi.loadFileFromS3(path, nurseryId);
    const base64 = this.convertArrayBufferToBase64(data);
    return `data:image/webp;base64,${base64}`;
  }

  /**
   * getPhotoItemList
   * @param nurseryId 施設ID
   * @param token トークン
   * @returns 結果
   */
  public async getPhotoItemList(nurseryId: string, token: string | null) {
    const result = await this.photoItemRepository.getPublicList(nurseryId, token);
    return result;
  }

  /**
   * 指定グループの注文一覧を取得
   * @param token トークン
   * @returns 注文一覧
   */
  public async getPhotoOrderList(token: string | null) {
    const result = await this.photoOrderRepository.getList(token);
    return result;
  }

  /**
   * photoPayByNursery
   * @param nurseryId 施設ID
   * @param salesGroupId 販売グループID
   * @param childId 子どもID
   * @param price 値段
   * @param photos 写真リスト
   * @returns 結果
   */
  public async photoPayByNursery(
    nurseryId: string,
    salesGroupId: SalesGroupId,
    childId: ChildId,
    price: number,
    photos: {
      photoId: PhotoId,
      photoItemId: PhotoItemId,
      numberOfItems: NumberOfItems,
    }[],
  ) {
    const result = await this.wagakoNurseryApi.photoPayByNursery(nurseryId, {
      salesGroupId: salesGroupId.value,
      childId: childId.value,
      photos: photos.map((v) => ({
        photoId: v.photoId.value,
        photoItemId: v.photoItemId.value,
        numberOfItems: v.numberOfItems.value,
      })),
      price,
    });
    return result;
  }

  /**
   * photoPayByCard
   * @param nurseryId 施設ID
   * @param salesGroupId 販売グループID
   * @param childId 子どもID
   * @param price 価格
   * @param photos 写真
   * @param cardId カードID
   * @returns 結果
   */
  public async photoPayByCard(
    nurseryId: string,
    salesGroupId: SalesGroupId,
    childId: ChildId,
    price: number,
    photos: {
      photoId: PhotoId,
      photoItemId: PhotoItemId,
      numberOfItems: NumberOfItems,
    }[],
    cardId: string,
  ) {
    const result = await this.wagakoNurseryApi.photoPayByCard(nurseryId, {
      salesGroupId: salesGroupId.value,
      childId: childId.value,
      photos: photos.map((v) => ({
        photoId: v.photoId.value,
        photoItemId: v.photoItemId.value,
        numberOfItems: v.numberOfItems.value,
      })),
      price,
      cardId,
    });
    return result;
  }

  /**
   * 注文のキャンセル
   * @param nurseryId 施設ID
   * @param photoOrder 写真注文
   * @returns 結果
   */
  public async cancelOrder(
    nurseryId: string,
    photoOrder: PhotoOrderEntity,
  ): Promise<{
    /** エラーコード */
    code: string,
    /** メッセージ */
    message: string,
  } | undefined> {
    const input = {
      createDateTime: photoOrder.createDatetime.value.toJSON(),
      salesGroupId: photoOrder.salesGroupId.value,
    };
    if (photoOrder.paymentType.value === 'creditCard') {
      // クレジットカード支払
      const result = await this.wagakoNurseryApi.cancelCreditCardPhotoOrder(nurseryId, input);
      if (result.statusCode !== 201) {
        if (result.statusCode === 406) {
          return {
            code: result.code,
            message: result.message,
          };
        }
        throw new Error(`クレジットカードキャンセルで予期せぬエラー:cancelOrder:${JSON.stringify(input)}:${JSON.stringify(result)}`);
      }
      return undefined;
    }
    // 施設支払い
    if (photoOrder.paymentStatus.value !== '未支払い') {
      return {
        code: 'PaymentStatusNotPayYet',
        /** メッセージ */
        message: '施設側での徴収の場合は、「未支払い」状態でないとキャンセルできません。施設にご相談ください',
      };
    }
    const result = await this.wagakoNurseryApi.cancelPhotoOrder(nurseryId, input);
    if (result.statusCode !== 201) {
      throw new Error(`写真支払いキャンセルで予期せぬエラー:cancelOrder:${JSON.stringify(input)}:${JSON.stringify(result)}`);
    }
    return undefined;
  }

  private convertArrayBufferToBase64(data: ArrayBuffer) {
    let binary = '';
    const bytes = new Uint8Array(data);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}
